import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import SendIcon from "@mui/icons-material/Send";
import MicIcon from "@mui/icons-material/Mic";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ReplyIcon from "@mui/icons-material/Reply";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";

import {
  useConversation,
  useMessageComposer,
  useSpeechToText,
  useTranslationFlag,
  useTypingIndicator,
  useAutoResizeTextarea,
  normalizeMessage,
  removeMessageById,
  handleEnterKey,
  QUICK_REACTIONS,
} from "@lobees/chat-core";

import * as url from "helper/UrlHelper";
import AttachmentThumbnail from "./AttachmentThumbnail";

// The reactchat endpoint (and the socket echo of it) return the raw
// mongoose subdocs ({sender, emoji}) — reshape to the {senderId, emoji}
// shape normalizeMessage already uses so both paths produce the same thing.
const normalizeReactions = (reactions = []) =>
  (reactions || []).map((reaction) => ({
    senderId: reaction.sender != null ? reaction.sender.toString() : null,
    emoji: reaction.emoji ?? "",
  }));

// Collapses the per-sender reaction list into one row per emoji with a count,
// e.g. two 👍 reactions render as a single "👍 2" chip rather than duplicates.
const summarizeReactions = (reactions = [], currentUserId) => {
  const byEmoji = new Map();
  (reactions || []).forEach((reaction) => {
    const entry = byEmoji.get(reaction.emoji) ?? { emoji: reaction.emoji, count: 0, mine: false };
    entry.count += 1;
    if (currentUserId != null && reaction.senderId === String(currentUserId)) {
      entry.mine = true;
    }
    byEmoji.set(reaction.emoji, entry);
  });
  return Array.from(byEmoji.values());
};

const ConversationRightContactChatPanel = ({ isMobile = false }) => {
  const { conversation, chatClient, adapter, currentUser, resetConversation } = useConversation();

  const [chatMessages, setChatMessages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);

  const roomId = conversation?.roomId;
  const contactUserId = conversation?.id;

  const getAllChatMessages = async () => {
    if (!contactUserId) return;
    try {
      setIsLoading(true);
      const response = await chatClient.fetchMessages(contactUserId);
      setIsLoading(false);
      if (response.status) {
        setChatMessages(
          response.data.map((raw) => normalizeMessage(raw, { currentUserId: currentUser?.id }))
        );
      }
    } catch (error) {
      setIsLoading(false);
      console.log(error.message);
    }
  };

  useEffect(() => {
    getAllChatMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactUserId, roomId]);

  // join/leave the socket room for this conversation + listen for incoming
  // real-time messages, mirroring lobees-web's ConversationRightContactChatPanel.
  useEffect(() => {
    if (!roomId) return undefined;

    adapter.joinRoom(roomId);

    const offMessage = adapter.onMessage((data) => {
      if (data) {
        setChatMessages((prev) => [
          ...prev,
          normalizeMessage(data, { currentUserId: currentUser?.id }),
        ]);
      }
    });

    const offMessageDeleted = adapter.onMessageDeleted((messageId) => {
      if (messageId) {
        setChatMessages((prev) => removeMessageById(prev, messageId));
      }
    });

    const offMessageReacted = adapter.onMessageReacted(({ messageid, reactions }) => {
      if (!messageid) return;
      setChatMessages((prev) =>
        prev.map((message) =>
          message.id === messageid
            ? { ...message, reactions: normalizeReactions(reactions) }
            : message
        )
      );
    });

    return () => {
      offMessage?.();
      offMessageDeleted?.();
      offMessageReacted?.();
      adapter.leaveRoom(roomId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const { typingUser, notifyTyping } = useTypingIndicator({
    adapter,
    roomId,
    senderId: currentUser?.id,
    senderLabel: currentUser?.name,
  });

  const { languageList, selectedLanguage, flaggedLanguage, selectLanguage } =
    useTranslationFlag();

  const { isSupported: micSupported, isListening, toggle: toggleMic } = useSpeechToText({
    language: flaggedLanguage,
    onFinalTranscript: (text) => composer.setMessageText((prev) => (prev ? `${prev} ${text}` : text)),
  });

  const composer = useMessageComposer({
    chatClient,
    adapter,
    buildMessagePayload: (draft) => ({
      contactid: roomId,
      sender: currentUser?.id,
      receiver: contactUserId,
      sendermail: currentUser?.email,
      receivermail: conversation?.email,
      description: draft.body,
      moduleslug: "MOD_CONTACT",
      uploads: draft.uploads,
      // The backend echoes back whatever `uploadedchatfiles` it was sent in
      // its response rather than re-deriving it from `uploads` — omitting
      // this meant the confirmed message that replaces the optimistic one
      // came back with no attachments, even though the files were genuinely
      // uploaded (a fresh GET would show them again, since that endpoint
      // does populate it). Matches lobees-web's sendMessageHandler payload.
      uploadedchatfiles: draft.attachments,
      sendername: `${currentUser?.name ?? ""} ${currentUser?.surname ?? ""}`.trim(),
      senderimage: currentUser?.photoPath ?? "",
      flaggedlanguage: flaggedLanguage,
      parent: draft.parent,
      parentchatmessage: draft.parentMessage,
      parentchatsender: draft.parentSender,
      broadcast: { chatroomid: roomId, receiverlist: [contactUserId] },
    }),
    onOptimisticMessage: (message) => setChatMessages((prev) => [...prev, message]),
    onMessageConfirmed: (tempId, confirmed) =>
      setChatMessages((prev) =>
        prev.map((message) =>
          message.tempId === tempId
            ? normalizeMessage(confirmed, { currentUserId: currentUser?.id })
            : message
        )
      ),
    onMessageFailed: (tempId) =>
      setChatMessages((prev) =>
        prev.map((message) => (message.tempId === tempId ? { ...message, status: "failed" } : message))
      ),
  });

  useAutoResizeTextarea(textareaRef, [composer.messageText]);

  const handleSend = () => {
    // Files upload as soon as they're attached (see handleFileChange), not
    // as part of send — but a fast Enter-key send could still race an
    // in-flight upload and go out without the attachment. Block that rather
    // than silently drop the file.
    if (composer.isUploading) return;
    composer.send();
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      await composer.uploadFiles(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  // Images open full-size in a new tab; everything else lets the browser's
  // own content-type handling decide (preview or download) — a proper
  // in-app lightbox is a reasonable follow-up but not required for this pass.
  const openAttachment = (file) => {
    window.open(url.SERVER_URL + file.path, "_blank", "noopener,noreferrer");
  };

  const handleReplyClick = (message) => {
    composer.setReply(message, message.own ? "You" : conversation?.name ?? message.senderLabel);
    textareaRef.current?.focus();
  };

  const handleDeleteMessage = async (message) => {
    if (!message?.id) return;
    if (!window.confirm("Delete this message? This can't be undone.")) return;

    try {
      await chatClient.deleteChat(message.id, {
        contactid: roomId,
        constactperson: contactUserId,
      });
      setChatMessages((prev) => removeMessageById(prev, message.id));
      adapter.emitDeleteChatMessage({
        chatroomid: roomId,
        messageid: message.id,
        receiverlist: [contactUserId],
      });
    } catch (error) {
      console.log(error.message);
    }
  };

  const handleReact = async (message, emoji) => {
    if (!message?.id) return;

    try {
      const response = await chatClient.reactToChat(message.id, { emoji });
      const reactions = normalizeReactions(response.data);

      setChatMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, reactions } : m))
      );

      adapter.emitReactToMessage({
        chatroomid: roomId,
        messageid: message.id,
        reactions: response.data,
        receiverlist: [contactUserId],
      });
    } catch (error) {
      console.log(error.message);
    }
  };

  // Starring is a private per-user bookmark (like Gmail), not something the
  // other party in the conversation needs to see — so this only calls the
  // REST endpoint and updates local state, no socket broadcast.
  const handleToggleStar = async (message) => {
    if (!message?.id) return;

    try {
      await chatClient.toggleStarChat(message.id, {});
      setChatMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, starredByMe: !m.starredByMe } : m))
      );
    } catch (error) {
      console.log(error.message);
    }
  };

  // Groups consecutive messages sent on the same calendar day under one date
  // divider, gtalk/WhatsApp-style, so each bubble only needs to show a time —
  // the date is established once per group rather than repeated per message.
  const messageGroups = useMemo(() => {
    const groups = [];
    chatMessages.forEach((message) => {
      const dateKey = message.createdAt ? new Date(message.createdAt).toDateString() : "";
      const lastGroup = groups[groups.length - 1];
      if (lastGroup && lastGroup.dateKey === dateKey) {
        lastGroup.messages.push(message);
      } else {
        groups.push({ dateKey, messages: [message] });
      }
    });
    return groups;
  }, [chatMessages]);

  const formatDateDivider = (dateKey) => {
    if (!dateKey) return "";
    const date = new Date(dateKey);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return date.toLocaleDateString([], { day: "numeric", month: "long", year: "numeric" });
  };

  if (!conversation) {
    return null;
  }

  return (
    <Box sx={{ flex: 1, width: "100%", display: "flex", flexDirection: "column", minWidth: 0 }}>
      {/* header */}
      <Box
        sx={{
          width: "100%",
          p: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {isMobile ? (
          <IconButton onClick={resetConversation} title="Back to contacts" edge="start">
            <ArrowBackIcon />
          </IconButton>
        ) : null}

        <Avatar src={conversation.logo ? url.SERVER_URL + conversation.logo : undefined}>
          {conversation.name?.charAt(0)}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1">{conversation.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {conversation.email}
          </Typography>
        </Box>
        <Select
          size="small"
          value={selectedLanguage?.value ?? "en"}
          onChange={(e) => selectLanguage(e.target.value)}
        >
          {languageList.map((item) => (
            <MenuItem key={item.value} value={item.value}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* messages */}
      <Box sx={{ flex: 1, width: "100%", minWidth: 0, overflowX: "hidden", overflowY: "auto", p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : chatMessages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
            Let's start the discussion with a message
          </Typography>
        ) : (
          messageGroups.map((group) => (
            <Box key={group.dateKey} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", justifyContent: "center", my: 0.5 }}>
                <Chip size="small" label={formatDateDivider(group.dateKey)} sx={{ bgcolor: "grey.200" }} />
              </Box>

              {/* Extra gap (vs. the 1.5 used elsewhere) so the floating hover
                  toolbar — anchored above each message — has room to sit in
                  the space between two consecutive same-side messages without
                  bleeding into the previous bubble's text (an issue that only
                  showed up for short one-line messages stacked tightly). */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 4.5 }}>
              {group.messages.map((message, index) => (
                <Box
                  key={message.id ?? message.tempId ?? index}
                  sx={{
                    position: "relative",
                    alignSelf: message.own ? "flex-end" : "flex-start",
                    maxWidth: "75%",
                    minWidth: 0,
                    "&:hover .msg-hover-toolbar": { opacity: 1, pointerEvents: "auto" },
                  }}
                >
                  {/* Floating action toolbar — hidden until the message is hovered,
                      mirroring the reference chat apps' inline icon toolbar rather
                      than a click-to-open menu. For a contact message (left-aligned)
                      it starts flush with the bubble's left/start edge and grows
                      rightward, over the bubble; for your own message (right-aligned)
                      it ends flush with the bubble's right/end edge and grows
                      leftward — either way it grows inward, over available space,
                      never past the panel's edge where it'd get clipped by the
                      messages container's overflowX: hidden. */}
                  <Box
                    className="msg-hover-toolbar"
                    sx={{
                      position: "absolute",
                      bottom: "100%",
                      // A visible gap here (previously mb: 0.5) is a dead zone: moving
                      // the cursor from the bubble up to the toolbar crosses empty
                      // space, the row loses :hover for an instant, and the toolbar's
                      // opacity/pointerEvents snap off — pointerEvents: none then
                      // blocks it from ever re-acquiring hover even if the cursor lands
                      // back on the same spot, so it stays stuck hidden. A couple px of
                      // overlap keeps the two boxes touching with zero dead space.
                      mb: "-2px",
                      [message.own ? "right" : "left"]: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                      bgcolor: "background.paper",
                      borderRadius: 5,
                      boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                      px: 0.25,
                      py: 0,
                      opacity: 0,
                      pointerEvents: "none",
                      transition: "opacity 0.12s ease",
                      zIndex: 2,
                      "& .MuiIconButton-root": { p: 0.5 },
                    }}
                  >
                    {QUICK_REACTIONS.map((emoji) => (
                      <IconButton
                        key={emoji}
                        size="small"
                        onClick={() => handleReact(message, emoji)}
                        title={`React with ${emoji}`}
                        sx={{
                          bgcolor: message.reactions?.some(
                            (r) => r.senderId === String(currentUser?.id) && r.emoji === emoji
                          )
                            ? "action.selected"
                            : "transparent",
                        }}
                      >
                        <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                          {emoji}
                        </Box>
                      </IconButton>
                    ))}
                    <IconButton
                      size="small"
                      onClick={() => handleToggleStar(message)}
                      title={message.starredByMe ? "Unstar" : "Mark as important"}
                    >
                      {message.starredByMe ? (
                        <StarIcon fontSize="small" sx={{ color: "#f5b400" }} />
                      ) : (
                        <StarBorderIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleReplyClick(message)} title="Reply">
                      <ReplyIcon fontSize="small" />
                    </IconButton>
                    {message.own ? (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteMessage(message)}
                        title="Delete"
                        sx={{ color: "error.main" }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                  </Box>

                  {message.starredByMe ? (
                    <StarIcon
                      sx={{
                        position: "absolute",
                        top: -6,
                        [message.own ? "right" : "left"]: -6,
                        fontSize: 16,
                        color: "#f5b400",
                        bgcolor: "background.paper",
                        borderRadius: "50%",
                        p: 0.2,
                        boxShadow: 1,
                        zIndex: 1,
                      }}
                    />
                  ) : null}

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
                    <Box
                      sx={{
                        bgcolor: message.own ? "primary.main" : "background.paper",
                        color: message.own ? "primary.contrastText" : "text.primary",
                        border: message.own ? "none" : "1px solid",
                        borderColor: "divider",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                        borderRadius: 2,
                        px: 1.5,
                        py: 1,
                        minWidth: 0,
                        opacity: message.status === "sending" ? 0.6 : message.status === "failed" ? 0.5 : 1,
                      }}
                    >
                      {message.parent ? (
                        <Box
                          sx={{
                            mb: 1,
                            pl: 1,
                            py: 0.25,
                            borderLeft: "3px solid",
                            borderColor: message.own ? "rgba(255,255,255,0.6)" : "primary.main",
                            opacity: 0.85,
                            minWidth: 0,
                          }}
                        >
                          <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                            {message.parentSender}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", wordBreak: "break-word", overflowWrap: "anywhere" }}
                          >
                            {message.parentMessage}
                          </Typography>
                        </Box>
                      ) : null}

                      {message.body ? (
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                        >
                          {message.body}
                        </Typography>
                      ) : null}
                      {message.translation ? (
                        <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid rgba(0,0,0,0.1)" }}>
                          <Typography
                            variant="caption"
                            sx={{ display: "block", wordBreak: "break-word", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
                          >
                            {message.translation.body}
                          </Typography>
                        </Box>
                      ) : null}
                      {message.attachments?.length > 0 ? (
                        <Box sx={{ mt: message.body ? 1 : 0, display: "flex", flexWrap: "wrap", gap: 1 }}>
                          {message.attachments.map((file, fileIndex) => (
                            <AttachmentThumbnail
                              key={file._id ?? fileIndex}
                              file={file}
                              size={72}
                              onOpen={() => openAttachment(file)}
                            />
                          ))}
                        </Box>
                      ) : null}
                    </Box>
                    {message.reactions?.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          alignSelf: message.own ? "flex-end" : "flex-start",
                        }}
                      >
                        {summarizeReactions(message.reactions, currentUser?.id).map((summary) => (
                          <Chip
                            key={summary.emoji}
                            size="small"
                            label={summary.count > 1 ? `${summary.emoji} ${summary.count}` : summary.emoji}
                            onClick={() => handleReact(message, summary.emoji)}
                            sx={{
                              height: 22,
                              fontSize: 13,
                              bgcolor: summary.mine ? "primary.light" : "grey.200",
                            }}
                          />
                        ))}
                      </Box>
                    ) : null}
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title={new Date(message.createdAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
                      sx={{ px: 0.5, alignSelf: message.own ? "flex-end" : "flex-start" }}
                    >
                      {message.status === "failed" ? "Failed to send · " : ""}
                      {new Date(message.createdAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))}
              </Box>
            </Box>
          ))
        )}

        {typingUser ? (
          <Chip size="small" label={`${typingUser.label} is typing…`} sx={{ alignSelf: "flex-start" }} />
        ) : null}

        <div ref={messagesEndRef} />
      </Box>

      {/* composer */}
      <Box sx={{ width: "100%", borderTop: "1px solid", borderColor: "divider" }}>
        {composer.parentMessageId ? (
          <Box sx={{ width: "100%", px: 2, pt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                borderLeft: "3px solid",
                borderColor: "primary.main",
                bgcolor: "grey.100",
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
              }}
            >
              <Typography variant="caption" sx={{ display: "block", fontWeight: 600 }}>
                Replying to {composer.replyUserName}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                sx={{ display: "block" }}
              >
                {composer.replyMessage}
              </Typography>
            </Box>
            <IconButton size="small" onClick={composer.clearReply} title="Cancel reply">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        ) : null}

        {/* attachment tray: thumbnail preview of what's about to be sent, removable before send */}
        {composer.isUploading || composer.uploadedFiles.length > 0 || composer.fileTooBig ? (
          <Box sx={{ width: "100%", px: 2, pt: 1.5, display: "flex", flexWrap: "wrap", alignItems: "flex-start", gap: 1.5 }}>
            {composer.uploadedFiles.map((file, index) => (
              <AttachmentThumbnail
                key={index}
                file={file}
                onRemove={() => composer.removeUploadedFile(index)}
              />
            ))}

            {/* One placeholder per file currently uploading — each swaps for
                its real thumbnail the moment the server returns its path,
                rather than one generic "uploading…" status with no per-file
                feedback. */}
            {composer.isUploading
              ? Array.from({ length: composer.uploadedFileCount }).map((_, index) => (
                  <AttachmentThumbnail key={`uploading-${index}`} loading />
                ))
              : null}

            {composer.fileTooBig ? (
              <Typography variant="caption" color="error" sx={{ width: "100%" }}>
                One or more files exceed the 12.5MB limit and weren't attached.
              </Typography>
            ) : null}
          </Box>
        ) : null}

        <Box sx={{ width: "100%", p: 2, display: "flex", alignItems: "flex-end", gap: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFileChange}
          />
          <IconButton onClick={() => fileInputRef.current?.click()} title="Attach files">
            <AttachFileIcon />
          </IconButton>

          {micSupported ? (
            <IconButton
              color={isListening ? "primary" : "default"}
              onClick={toggleMic}
              title={isListening ? "Stop listening" : "Tap to speak"}
            >
              <MicIcon />
            </IconButton>
          ) : null}

          <TextField
            inputRef={textareaRef}
            fullWidth
            multiline
            maxRows={6}
            placeholder="Enter your message"
            value={composer.messageText}
            onChange={(e) => {
              composer.setMessageText(e.target.value);
              notifyTyping();
            }}
            onKeyDown={(e) => handleEnterKey(e, handleSend)}
          />

          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={composer.isSending || composer.isUploading}
            title={composer.isUploading ? "Waiting for attachment to finish uploading…" : undefined}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ConversationRightContactChatPanel;
